import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Expand, Play } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/api";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { Reveal } from "@/components/Motion";
import { WatermarkOverlay } from "@/components/WatermarkOverlay";
import { Slideshow } from "@/components/Slideshow";
import { SubscribeCard } from "@/components/SubscribeCard";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatPrice, slugifyName } from "@/lib/utils";

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [slideIndex, setSlideIndex] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    Promise.all([api.get(`/events/${slug}`), api.get(`/events/${slug}/photos`)])
      .then(([eventResponse, photosResponse]) => {
        setEvent(eventResponse.data);
        setPhotos(photosResponse.data);
      })
      .catch(() => setEvent(false));
  }, [slug]);

  if (event === false) {
    return <div data-testid="event-not-found" className="flex min-h-screen items-center justify-center bg-onyx px-4 text-center text-zinc-400">Event not found.</div>;
  }
  if (!event) return <div data-testid="event-loading-state" className="min-h-screen bg-onyx pt-40 text-center font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading gallery</div>;

  return (
    <div data-testid="event-gallery-page" className="bg-onyx">
      <Seo title={event.title} description={`${event.title} photography album from ${event.location}. Preview watermarked frames and buy high-resolution digital downloads.`} image={event.cover_url ? new URL(event.cover_url, window.location.origin).href : undefined} />
      <section className="relative flex min-h-[74vh] items-end overflow-hidden pt-32">
        <img src={event.cover_url} alt={`${event.title} cover frame`} className="absolute inset-0 h-full w-full object-cover opacity-62" />
        <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/60 to-onyx/20" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-4 pb-16 sm:px-8 lg:px-12">
          <Link data-testid="back-to-events-link" to="/events" className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.28em] text-titanium hover:text-gold"><ArrowLeft className="h-4 w-4" /> Event library</Link>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">{event.date} / {event.location}</p>
              <h1 className="mt-5 font-display text-5xl font-light leading-none text-white sm:text-7xl">{event.title}</h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-zinc-300">{event.description}</p>
            </div>
            <div className="border border-gold/25 bg-black/55 p-6 backdrop-blur-xl lg:col-span-4">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Complete album</p>
              <div className="mt-4 flex items-end justify-between gap-4">
                <span data-testid="album-price" className="font-display text-5xl text-white">${Number(event.album_price).toFixed(0)}</span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{photos.length} frames</span>
              </div>
              <Button data-testid="buy-full-album-button" onClick={() => addItem({ item_type: "album", item_id: event.id, title: event.title, price: event.album_price })} className="mt-6 h-13 w-full rounded-none bg-gold py-6 font-mono text-xs uppercase tracking-[0.24em] text-onyx hover:bg-titanium">
                <Download className="h-4 w-4" /> Buy full album
              </Button>
              <button data-testid="play-slideshow-button" onClick={() => setSlideIndex(0)} className="mt-3 flex w-full items-center justify-center gap-2 border border-white/20 py-4 font-mono text-xs uppercase tracking-[0.24em] text-white transition-colors hover:border-gold hover:text-gold">
                <Play className="h-4 w-4" /> Play slideshow
              </button>
              <ShareBar testId="event-share-bar" title={`${event.title} — mellowjmedia`} text={`${event.title} (${event.date} · ${event.location}) — browse the full photography album on mellowjmedia.`} url={window.location.href} />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1600px] px-4 pt-14 sm:px-8 lg:px-12">
        <SubscribeCard event={event} />
      </div>

      <section className="mx-auto max-w-[1600px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <Reveal className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">Watermarked previews</p>
            <h2 className="mt-3 font-display text-4xl font-light text-white">Select your frames.</h2>
          </div>
          <p className="max-w-md text-sm leading-7 text-zinc-500">Previews are freely browsable. Purchased files are delivered without the watermark in high resolution.</p>
        </Reveal>
        <div className="columns-1 gap-5 space-y-5 sm:columns-2 lg:columns-3">
          {photos.map((photo, index) => (
            <motion.article key={photo.id} data-testid={`photo-preview-thumbnail-${photo.id}`} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.04, duration: 0.65 }} className="group relative break-inside-avoid overflow-hidden border border-white/10 bg-carbon">
              <button data-testid={`open-photo-preview-${photo.id}`} onClick={() => setSlideIndex(index)} className="relative block w-full text-left">
                <img src={photo.preview_url} alt={`${photo.title} watermarked preview`} className="w-full object-cover transition duration-700 group-hover:scale-[1.025]" />
                <WatermarkOverlay />
                <span className="absolute right-4 top-4 border border-white/15 bg-black/55 p-2 text-gold opacity-0 backdrop-blur transition-opacity group-hover:opacity-100"><Expand className="h-4 w-4" /></span>
              </button>
              <div className="flex items-center justify-between gap-4 p-5">
                <div>
                  <h3 className="font-display text-2xl text-white">{photo.title}</h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    {photo.vehicle || ""}
                    {photo.rider && <>{photo.vehicle ? " · " : ""}<Link data-testid={`rider-link-${photo.id}`} to={`/riders/${slugifyName(photo.rider)}`} className="text-gold transition-colors hover:text-titanium">{photo.rider}</Link></>}
                    {!photo.vehicle && !photo.rider && event.category}
                  </p>
                </div>
                <button data-testid={`buy-single-photo-button-${photo.id}`} onClick={() => addItem({ item_type: "photo", item_id: photo.id, title: photo.title, price: photo.price })} className="border border-gold/35 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-onyx">
                  ${formatPrice(photo.price)}
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <Slideshow photos={photos} event={event} index={slideIndex} onClose={() => setSlideIndex(null)} onIndexChange={setSlideIndex} />
    </div>
  );
}
